import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBell } from '@fortawesome/free-solid-svg-icons';
import modaleContentFile from '../../data/modaleContent.json';
import ModaleContact from '../ModaleContact/ModaleContact';
import ModaleOuverture from '../ModaleOuverture/ModaleOuverture';
import { useHoraire, useNow } from '../../lib/customHooks';
import { parseHoraires, getStatus } from '../../utils/horaires';
import './Header.scss';

const BELL_STORAGE_KEY = 'bellSeenMessage';

function Header() {
    const [isContactModalOpen, setContactModalOpen] = useState(false);
    const [isModaleOpen, setIsModaleOpen] = useState(false);
    const [bellSeen, setBellSeen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [modaleContent, setModaleContent] = useState({
        active: modaleContentFile.active !== false,
        titre: modaleContentFile.titre || '',
        message: modaleContentFile.message || '',
    });
    const location = useLocation();
    const navigate = useNavigate();

    const horaire = useHoraire();
    const now = useNow();
    const schedule = useMemo(() => parseHoraires(horaire), [horaire]);
    const status = getStatus(schedule, now);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        fetch('/api/modale')
            .then(r => r.ok ? r.json() : null)
            .then(data => {
                if (!data) return;
                setModaleContent({
                    active: data.active !== false,
                    titre: data.titre || '',
                    message: data.message || '',
                });
            })
            .catch(() => {});
    }, []);

    const hasMessage = (modaleContent.titre && modaleContent.titre.trim() !== '') || (modaleContent.message && modaleContent.message.trim() !== '');
    const announcementEnabled = modaleContent.active && hasMessage;
    const bellActive = announcementEnabled && !bellSeen;

    useEffect(() => {
        if (announcementEnabled) {
            const storedKey = `${modaleContent.titre}|${modaleContent.message}`;
            const stored = localStorage.getItem(BELL_STORAGE_KEY);
            if (stored === storedKey) {
                setBellSeen(true);
            } else {
                localStorage.removeItem(BELL_STORAGE_KEY);
                setBellSeen(false);
            }
        }
    }, [announcementEnabled, modaleContent.message, modaleContent.titre]);

    const toggleContactModal = () => {
        setContactModalOpen(!isContactModalOpen);
    };

    const handleBellClick = () => {
        setIsModaleOpen(true);
        if (announcementEnabled && !bellSeen) {
            localStorage.setItem(BELL_STORAGE_KEY, `${modaleContent.titre}|${modaleContent.message}`);
            setBellSeen(true);
        }
    };

    // Ancres vers les sections de l'accueil, depuis n'importe quelle page.
    const goToSection = (id) => (e) => {
        e.preventDefault();
        if (location.pathname === '/') {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
            navigate(`/#${id}`);
        }
    };

    return (
        <div className={`header-wrapper ${scrolled ? 'header-wrapper--scrolled' : ''}`}>

            <header className="header">
                <Link to="/" className="logo" aria-label="Accueil BAsique">
                    <img src="/assets/phrase_header.png" alt="Vos courses bio et sans emballage à Roanne" />
                </Link>
                <nav className="navigation" aria-label="Navigation principale">
                    <Link to="/" className='nav-link'>Accueil</Link>
                    <a href="/#produits" className='nav-link' onClick={goToSection('produits')}>Produits</a>
                    <a href="/#vrac" className='nav-link' onClick={goToSection('vrac')}>Le vrac</a>
                    {status && (
                        <span className={`status-chip ${status.open ? '' : 'status-chip--closed'}`} title="Horaires de la boutique">
                            <i></i>
                            <span>{status.text}{status.sub ? ` · ${status.sub}` : ''}</span>
                        </span>
                    )}
                    <div className="contact-wrapper">
                        <button
                            type="button"
                            onClick={toggleContactModal}
                            className='nav-button'
                        >
                            Contact
                        </button>
                        {announcementEnabled && (
                            <button
                                className={`cloche-btn ${bellActive ? 'cloche-btn--active' : 'cloche-btn--inactive'}`}
                                onClick={handleBellClick}
                                aria-label="Actualités"
                            >
                                <FontAwesomeIcon icon={faBell} />
                            </button>
                        )}
                    </div>
                </nav>
                {isContactModalOpen && <ModaleContact onClose={toggleContactModal} />}
                {isModaleOpen && (
                    <ModaleOuverture
                        titre={modaleContent.titre}
                        message={modaleContent.message}
                        onClose={() => setIsModaleOpen(false)}
                    />
                )}
            </header>

        </div>
    );
}

export default Header;
